import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { useSimulator } from "../Simulator/SimulatorStore";

export type EfficiencyDatum = {
  label: string;
  efficiency: number; // %
  rework: number; // %
};

Chart.register(...registerables);

export default function ProcessEfficiencyChart() {
  const { processes } = useSimulator();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    const labels = processes.map((p) => p.base.name);
    const effValues = processes.map((p) => Math.max(0, Math.min(100, 100 - p.derived.effectiveReworkPct)));
    const rewValues = processes.map((p) => Math.max(0, Math.min(100, p.derived.effectiveReworkPct)));

    // Gradients to match original SVG fills
    const effGrad = ctx.createLinearGradient(0, 0, 0, 300);
    effGrad.addColorStop(0, "#34D399");
    effGrad.addColorStop(1, "#059669");
    const rewGrad = ctx.createLinearGradient(0, 0, 0, 300);
    rewGrad.addColorStop(0, "#FBBF24");
    rewGrad.addColorStop(1, "#D97706");

    if (!chartRef.current) {
      chartRef.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Efficiency",
              data: effValues,
              backgroundColor: effGrad,
              borderColor: "#047857",
              borderWidth: 1,
              borderRadius: 6,
              borderSkipped: "bottom",
              barPercentage: 0.9,
              categoryPercentage: 0.9,
            },
            {
              label: "Rework Rate",
              data: rewValues,
              backgroundColor: rewGrad,
              borderColor: "#B45309",
              borderWidth: 1,
              borderRadius: 6,
              borderSkipped: "bottom",
              barPercentage: 0.9,
              categoryPercentage: 0.9,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: false,
              ticks: { color: "#666", font: { size: 12 } },
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              suggestedMax: 60,
              ticks: { stepSize: 15, color: "#666", font: { size: 12 } },
              grid: { color: "#e5e7eb" },
              title: { display: false },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: "index",
              intersect: false,
              callbacks: {
                title: (items) => (items && items.length ? items[0].label || "" : ""),
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
              },
            },
          },
        },
      });
    } else {
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets[0].data = effValues as any;
      chartRef.current.data.datasets[1].data = rewValues as any;
      chartRef.current.update();
    }

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [processes.map((p) => p.derived.effectiveReworkPct).join("|")]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-2">Process Efficiency</h4>
      <div className="relative" style={{ height: 300 }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
